import React from 'react';

/**
 * Small, safe Markdown renderer for LLM-written briefings.
 *
 * Blocks: # headings, a line that is only **Bold** (treated as a section
 * heading), "- " / "* " bullet lists, "1. " numbered lists, paragraphs.
 * Inline: **bold**, `code`, [text](https://...) links (http/https only).
 * Never uses innerHTML; colours come from theme tokens (see .md-* in theme.css).
 */

const INLINE_RE = /(\*\*(.+?)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))/;

function renderInline(text) {
  const parts = [];
  let rest = text;
  let key = 0;
  while (rest) {
    const m = rest.match(INLINE_RE);
    if (!m) {
      parts.push(rest);
      break;
    }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[1]) {
      parts.push(<strong key={key++} className="md-strong">{m[2]}</strong>);
    } else if (m[3]) {
      parts.push(<code key={key++} className="md-code">{m[4]}</code>);
    } else {
      const href = /^https?:\/\//i.test(m[7]) ? m[7] : undefined;
      parts.push(
        <a key={key++} className="md-link" href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          {m[6]}
        </a>
      );
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}

function parseBlocks(text) {
  const blocks = [];
  let para = [];
  let list = null;

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'p', text: para.join(' ') });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push(list);
    list = null;
  };

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    const boldHeading = line.match(/^\*\*([^*]+)\*\*:?$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    const numbered = line.match(/^(\d+)[.)]\s+(.*)$/);

    if (heading || boldHeading) {
      flushPara();
      flushList();
      blocks.push({ type: 'h', level: heading ? heading[1].length : 3, text: heading ? heading[2] : boldHeading[1] });
    } else if (bullet || numbered) {
      flushPara();
      const type = bullet ? 'ul' : 'ol';
      if (!list || list.type !== type) {
        flushList();
        list = { type, items: [] };
      }
      list.items.push(bullet ? bullet[1] : numbered[2]);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return blocks;
}

const MarkdownRenderer = ({ content, className = '' }) => {
  if (!content) return null;
  const blocks = parseBlocks(String(content));
  return (
    <div className={`md ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === 'h') {
          const Tag = b.level <= 2 ? 'h3' : 'h4';
          return <Tag key={i} className={`md-h md-h${Math.min(b.level, 4)}`}>{renderInline(b.text)}</Tag>;
        }
        if (b.type === 'ul' || b.type === 'ol') {
          const Tag = b.type;
          return (
            <Tag key={i} className={`md-list md-${b.type}`}>
              {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
            </Tag>
          );
        }
        return <p key={i} className="md-p">{renderInline(b.text)}</p>;
      })}
    </div>
  );
};

export default MarkdownRenderer;
